import React, { useRef } from 'react'
import { handlePrintData } from '../../../utils/PrintData'
import { PrintIcon } from '../../../utils/TableActionIcons'
import { formatDate, formatDateTime } from '../../../utils/FormateDateTime'
import ReportHeader from '../../../shared/ReportHeader'
import ReportFooter from '../../../shared/ReportFooter'
import ChaiReportHeader from '../../../shared/ChaiReportHeader'
import ChaiReportFooter from '../../../shared/ChaiReportFooter'
import useMedicalReportSettings from '../../../hooks/useMedicalReportSettings'
import { formatDuration } from '../../../utils/FormateDuration'
import { useTranslation } from 'react-i18next'

function ViewSickLeaveCertificate({ selectedLeaveData }) {
  const { t } = useTranslation()
  const printRef = useRef(null)
  const { isChaiFormat, businessInfo } = useMedicalReportSettings()

  return (
    <div ref={printRef} className='min-h-screen flex flex-col'>
      <div className='flex-1 sick-leave-print-wrap'>
        <style>
          {`
          @media print {
            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              font-size: 14px;
            }
            @page {
              size: A4 portrait;
              margin: 6mm 8mm;
            }
            div[ref],
            table, tr, td, th {
              page-break-inside: avoid !important;
            }
            .print\\:hidden {
              display: none !important;
            }
            .print\\:mt-16 {
              margin-top: 1rem !important;
            }
            .print\\:mb-2 {
              margin-bottom: 0.5rem !important;
            }
            .print\\:p-1 {
              padding: 0.25rem !important;
            }
            table {
              font-size: 13px !important;
            }
            .emr-print-footer {
              position: fixed !important;
              bottom: 0 !important;
              left: 0 !important;
              right: 0 !important;
              width: 95% !important;
              max-width: 95% !important;
              margin: 0 auto !important;
              background: white !important;
              z-index: 10 !important;
            }
            .sick-leave-print-wrap {
              padding-bottom: ${isChaiFormat ? '24mm !important' : '0 !important'};
            }
          }
        `}
        </style>

        <div
          className='absolute top-8 right-20 rtl:right-auto rtl:left-20 cursor-pointer text-gray-700 hover:text-gray-900 print:hidden'
          onClick={() =>
            handlePrintData(printRef?.current?.innerHTML, 'HomeCareResult')
          }
        >
          <PrintIcon iconSize={20} />
        </div>
        <div className='flex flex-col'>
          {isChaiFormat ? (
            <ChaiReportHeader
              reportName={'Sick Leave Certificate - شهادة إجازة مرضية'}
              businessInfo={businessInfo}
            />
          ) : (
            <ReportHeader
              reportName={'Sick Leave Certificate - شهادة إجازة مرضية'}
            />
          )}
        </div>
        <div className='bg-gray-100 border border-gray-300 rounded mb-4 print:mb-2'>
          <div className='p-2 flex justify-between gap-4 print:p-1'>
            <span className='text-base !font-bold'>
              Registration No :{' '}
              <span className='mx-2 text-base !font-semibold'>
                {selectedLeaveData?.emr_id}
              </span>
              : رقم التسجيل
            </span>
            <span className='text-base !font-bold'>
              Sick Leave Date :
              <span className='mx-2 text-base !font-semibold'>
                {formatDate(selectedLeaveData?.visit_date)}
              </span>{' '}
              : تاريخ الإجازة المرضية
            </span>
            <span className='text-base !font-bold'>
              Issued :
              <span className='mx-2 text-base !font-semibold'>
                {formatDateTime(selectedLeaveData?.created_at)}
              </span>{' '}
              : صدر في
            </span>
          </div>
        </div>

        <div className='border border-gray-300  rounded mb-4 print:mb-2'>
          <div className='bg-gray-100 rounded p-2 print:p-1'>
            <span className='text-base !font-bold'>
              Patient Information / معلومات المريض
            </span>
          </div>
          <table className='w-full text-base'>
            <tbody>
              <tr>
                <td className='border border-l-0  p-2 font-bold w-1/4'>
                  <span className='text-base !font-bold'>
                    Patient Name / اسم المريض:
                  </span>
                </td>
                <td className='border  p-2'>
                  <span className='text-base !font-semibold'>
                    {selectedLeaveData?.patient?.full_name}
                  </span>
                </td>
                <td className='border  p-2 font-bold w-1/4'>
                  <span className='text-base !font-bold'>
                    Nationality / الجنسية:
                  </span>
                </td>
                <td className='border border-r-0  p-2'>
                  <span className='text-base !font-semibold'>
                    {selectedLeaveData?.patient?.nationality || ''}
                  </span>
                </td>
              </tr>
              <tr>
                <td className='border border-l-0 p-2 font-bold'>
                  <span className='text-base !font-bold'>
                    DOB / تاريخ الميلاد:
                  </span>
                </td>
                <td className='border p-2'>
                  <span className='text-base !font-semibold'>
                    {formatDate(selectedLeaveData?.patient?.dob)}
                  </span>
                </td>
                <td className='border p-2 font-bold'>
                  <span className='text-base !font-bold'>
                    Gender / الجنس:
                  </span>
                </td>
                <td className='border border-r-0 p-2'>
                  <span className='text-base !font-semibold'>
                    {selectedLeaveData?.patient?.gender}
                  </span>
                </td>
              </tr>
              <tr>
                <td className='border border-b-0  border-l-0 p-2 font-bold'>
                  <span className='text-base !font-bold'>
                    MRN / رقم الملف الطبي:
                  </span>
                </td>
                <td className='border border-b-0 p-2'>
                  <span className='text-base !font-semibold'>
                    {selectedLeaveData?.patient?.file_no}
                  </span>
                </td>
                <td className='border border-b-0 p-2 font-bold'>
                  <span className='text-base !font-bold'>
                    National ID / رقم الهوية:
                  </span>
                </td>
                <td className='border border-r-0 border-b-0 p-2'>
                  <span className='text-base !font-semibold'>
                    {selectedLeaveData?.patient?.document_id || selectedLeaveData?.patient?.passport_no || selectedLeaveData?.patient?.visa_no}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className='border border-gray-300 rounded mb-4 print:mb-2'>
          <div className='bg-gray-100 rounded p-2 print:p-1 font-bold border-b-0 border-gray-300'>
            <span className='text-base !font-bold'>
              Medical Information / المعلومات الطبية
            </span>
          </div>
          <table className='w-full text-base'>
            <tbody>
              <tr>
                <td className='border border-l-0 p-2 font-bold w-1/4'>
                  <span className='text-base !font-bold'>
                    Patient Disposition / حالة المريض:
                  </span>
                </td>
                <td className='border border-r-0 p-2'>
                  <span className='text-base !font-semibold'>
                    {selectedLeaveData?.disposition || 'Outpatient treatment'}
                  </span>
                </td>
              </tr>
              <tr>
                <td className='border border-l-0 p-2 font-bold'>
                  <span className='text-base !font-bold'>
                    Duration of Illness / مدة المرض:
                  </span>
                </td>
                <td className='border border-r-0 p-2'>
                  <span className='text-base !font-semibold'>
                    {formatDuration(selectedLeaveData?.duration_of_illness, t) || '--'}
                  </span>
                </td>
              </tr>
              <tr>
                <td className='border border-l-0 p-2 font-bold'>
                  <span className='text-base !font-bold'>
                    Sick Leave Period / مدة الإجازة المرضية:
                  </span>
                </td>
                <td className='border border-r-0 p-2'>
                  <span className='text-base !font-semibold'>
                    {selectedLeaveData?.number_of_days} Day(s)
                  </span>
                </td>
              </tr>
              <tr>
                <td className='border border-l-0 p-2 font-bold'>
                  <span className='text-base !font-bold'>
                    Sick leave start from / من:
                  </span>
                </td>
                <td className='border border-r-0 p-2'>
                  <span className='text-base !font-semibold'>
                    {formatDate(selectedLeaveData?.admit_date)}
                    <span className='mx-2 font-bold'> To / إلى </span>
                    {formatDate(selectedLeaveData?.discharge_date)}
                  </span>
                </td>
              </tr>
              <tr>
                <td className='border border-l-0 border-b-0 p-2 font-bold'>
                  <span className='text-base !font-bold'>
                    Physician Remarks / ملاحظات الطبيب:
                  </span>
                </td>
                <td className='border border-r-0 border-b-0 p-2'>
                  <span className='text-base !font-semibold'>
                    {selectedLeaveData?.remarks || ''}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className='border border-gray-300 rounded mb-4 print:mb-2 overflow-hidden'>
          <div className='flex items-stretch'>
            <div className='bg-gray-100 p-2 print:p-1 w-[150px] border-r border-gray-300 flex items-center justify-center'>
              <span className='text-base !font-bold'>Diagnosis :</span>
            </div>
            <div className='flex-1'>
              <table className='w-full text-base text-center border-collapse'>
                <thead>
                  <tr className='bg-gray-100 uppercase tracking-wider border-b border-gray-300'>
                    <th className='border-r border-gray-300 p-1 font-bold w-1/5'>Code</th>
                    <th className='border-r border-gray-300 p-1 font-bold w-3/5'>Diagnosis Description</th>
                    <th className='p-1 font-bold w-1/5'>Diagnosis Type</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(selectedLeaveData?.diagnosis) && selectedLeaveData.diagnosis.length > 0 ? (
                    selectedLeaveData.diagnosis.map((diag, index) => (
                      <tr key={index} className='border-b last:border-b-0 border-gray-300'>
                        <td className='border-r border-gray-300 p-1 font-semibold'>{diag.codeId || diag.code || ''}</td>
                        <td className='border-r border-gray-300 p-1 text-left font-semibold'>{diag.codeDescription || diag.description || ''}</td>
                        <td className='p-1 font-semibold'>{diag.type || diag.diagnosis_type || ''}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className='border-r border-gray-300 p-1 font-semibold'></td>
                      <td className='border-r border-gray-300 p-1 text-left font-semibold'>
                        {typeof selectedLeaveData?.diagnosis === 'string' ? selectedLeaveData?.diagnosis : ''}
                      </td>
                      <td className='p-1 font-semibold'></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Chief Complaint */}
        <div className='mb-4 print:mb-2'>
          <div className='bg-gray-100 border border-gray-200 rounded p-4 print:p-2'>
            <p className='text-base !font-bold mb-2'>Chief Complaint / الشكوى الرئيسية : <span className='!font-semibold ml-2'>{selectedLeaveData?.significiant_signs || ''}</span></p>
          </div>
        </div>

        {/* Recommendation */}
        <div className='mb-4 print:mb-2'>
          <div className='bg-gray-100 border border-gray-200 rounded p-4 print:p-2'>
            <p className='text-base !font-bold mb-2'>Recommendation / التوصية : <span className='!font-semibold ml-2'>{selectedLeaveData?.recommendation || ''}</span></p>
          </div>
        </div>

        {/* <div className='border border-gray-300 rounded mb-4'>
          <div className='bg-gray-100 rounded p-2 font-semibold border-b '>
            <span className='text-14-dark !font-semibold'>
              Status / الحالة
            </span>
          </div>
          <div className='p-2 text-center'>
            <span
              className={`bg-green-100  ${selectedLeaveData?.status === 'Canceled'
                ? 'bg-red-100 text-red-800'
                : 'text-green-800'
                }  px-3 text-[12px] py-1 rounded-full font-semibold`}
            >
              {selectedLeaveData?.status} : <span></span>
              {formatDateTime(
                selectedLeaveData?.status === 'Canceled'
                  ? selectedLeaveData?.cancelled_at
                  : selectedLeaveData?.created_at
              )}
            </span>
          </div>
        </div> */}
        <div className='border border-gray-300 rounded mb-4 print:mb-2'>
          <div className='bg-gray-100 rounded p-2 print:p-1 font-bold'>
            <span className='text-base !font-bold'>
              For Official Use Only / للاستخدام الرسمي فقط
            </span>
          </div>
          <table className='w-full text-base'>
            <tbody>
              <tr>
                <td className='border border-l-0 p-2 font-bold w-1/4'>
                  <span className='text-base !font-bold'>
                    Physician Name / اسم الطبيب:
                  </span>
                </td>
                <td className='border p-2'>
                  <span className='text-base !font-bold'>
                    {selectedLeaveData?.practitioner?.name}
                  </span>
                </td>
                <td className='border p-2 font-bold w-1/4'>
                  <span className='text-base !font-bold'>
                    Physician Specialty / تخصص الطبيب:
                  </span>
                </td>
                <td className='border border-r-0 p-2'>
                  <span className='text-base !font-bold'>
                    {selectedLeaveData?.practitioner?.speciality?.name_en ||
                      'General Practitioner'}
                  </span>
                </td>
              </tr>
              <tr>
                <td className='border border-l-0 p-2 font-bold'>
                  <span className='text-base !font-bold'>
                    License No / رقم الترخيص:
                  </span>
                </td>
                <td className='border p-2'>
                  <span className='text-base !font-bold'>
                    {selectedLeaveData?.practitioner?.license || ''}
                  </span>
                </td>
                <td className='border p-2 font-bold'>
                  <span className='text-base !font-bold'>
                    Physician Signature / توقيع الطبيب:
                  </span>
                </td>
                <td className='border border-r-0 p-2'></td>
              </tr>
              <tr>
                <td className='border p-2 font-bold'>
                  <span className='text-base !font-bold'>
                    Medical Director / المدير الطبي - رقم الترخيص:
                  </span>
                </td>
                <td className='border border-b-0 border-l-0 p-2'>
                </td>
                <td className='border border-b-0 p-2 font-bold'>
                  <span className='text-base !font-bold'>
                    Stamp / الختم - توقيع الطبيب:
                  </span>
                </td>
                <td className='border border-b-0 border-r-0 p-2'>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className='flex gap-8 w-full justify-end mt-8 print:mt-4'>
          <div className='text-center'>
            <p className='text-[12px] mt-1'>Practitioner Stamp:</p>
            <img
              src={selectedLeaveData?.practitioner?.stamp}
              alt='stamp'
              className='w-[7rem] h-[7rem] print:w-[5rem] print:h-[5rem] border-gray-200'
            />
          </div>
          <div className='text-center'>
            <p className='text-[12px] mt-1'>Practitioner Signature:</p>
            <img
              src={selectedLeaveData?.practitioner?.signature}
              alt='signature'
              className='w-[7rem] h-[7rem] print:w-[5rem] print:h-[5rem] border-gray-200'
            />
          </div>
        </div>
      </div>
      <div className={`mt-auto ${isChaiFormat ? 'emr-print-footer' : ''}`}>
        {isChaiFormat ? (
          <>
            <div className='border-t-2 border-gray-200 mb-1' />
            <ChaiReportFooter
              businessInfo={businessInfo}
            />
          </>
        ) : (
          <ReportFooter />
        )}
      </div>
    </div>
  )
}

export default ViewSickLeaveCertificate
